import type React from "react";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {createSubscription, deleteSubscription, updateSubscription} from "../../api/tenantDetailApi";
import {previewSubscriptionUpgrade} from "../../api/paidUpgradeApi";
import {ApiError} from "../../api/apiRequest";
import {hasPreviewChanges, UpgradePreviewDialog} from "../paid-upgrade/UpgradePreviewDialog";
import type {AdminSubscriptionSaveRequest, SubscriptionUpgradePreview} from "@calendall/shared";
import type {Plan} from "../../store/plansSlice";
import type {Subscription} from "../../types/tenant";
import {
    getSubscriptionLifecycleSummary,
    hasActiveSubscriptionAccess,
    subscriptionStatusLabelKey,
    type SubscriptionLifecycleStatus,
} from "../../utils/subscriptionDates";
import {formatDate, formatEuroCents} from "../../util/format";
import {colors, type ChipTone} from "../../theme/tokens";
import {SectionCard} from "../ui/SectionCard";
import {StatusChip} from "../ui/StatusChip";

const STATUS_TONES: Record<SubscriptionLifecycleStatus, ChipTone> = {
    renewing: "success",
    cancellationScheduled: "warning",
    paymentActionRequired: "error",
    canceled: "neutral",
    replaced: "neutral",
    synchronizing: "info",
};

type EditorState = {mode: "create"} | {mode: "edit"; subscription: Subscription};

type SubscriptionsSectionProps = {
    tenantId: string;
    subscriptions: Subscription[];
    plans: Plan[];
    number: number;
    id: string;
    /** A subscription was created, changed or deleted; its own request has already persisted it. */
    onChanged: () => void;
};

type DeleteGate = {allowed: true} | {allowed: false; reasonKey: string};

/** Mirrors the backend: Stripe-linked records never; otherwise unpaid, and another subscription must keep access. */
function deleteGate(subscription: Subscription, all: Subscription[], now: Date): DeleteGate {
    if (subscription.autoRenewal) return {allowed: false, reasonKey: "DETAIL_STRIPE_SUB_CANNOT_DELETE"};
    const otherWithAccess = all.some((other) => other.id !== subscription.id && hasActiveSubscriptionAccess(other, now));
    if (subscription.amountPaidInCents > 0 || !otherWithAccess) return {allowed: false, reasonKey: "DETAIL_PAID_SUB_CANNOT_DELETE"};
    return {allowed: true};
}

export function SubscriptionsSection({tenantId, subscriptions, plans, number, id, onChanged}: SubscriptionsSectionProps) {
    const {t} = useTranslation();
    const [editor, setEditor] = useState<EditorState | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const now = new Date();
    const planName = (planId: string) => plans.find((plan) => plan.id === planId)?.name ?? planId;
    const selectedId = editor?.mode === "edit" ? editor.subscription.id : null;

    const handleDelete = async (subscription: Subscription) => {
        setDeleteError(null);
        try {
            await deleteSubscription(tenantId, subscription.id);
            if (selectedId === subscription.id) setEditor(null);
            onChanged();
        } catch (e) {
            setDeleteError(e instanceof Error ? e.message : t("DETAIL_DELETE_FAILED"));
        }
    };

    return (
            <SectionCard
                    id={id}
                    number={number}
                    title={t("DETAIL_SUBSCRIPTIONS")}
                    helper={subscriptions.length > 0 ? t("DETAIL_SUBSCRIPTIONS_HELPER", {count: subscriptions.length}) : undefined}
                    actions={
                        <Button variant="text" startIcon={<AddIcon />} onClick={() => setEditor({mode: "create"})}>
                            {t("DETAIL_ADD_SUBSCRIPTION")}
                        </Button>
                    }
            >
                {deleteError && <Alert severity="error" sx={{mb: 2}} onClose={() => setDeleteError(null)}>{deleteError}</Alert>}
                {subscriptions.length === 0 ? (
                        <Typography variant="body2" sx={{color: colors.muted}}>{t("DETAIL_NO_SUBSCRIPTIONS")}</Typography>
                ) : (
                        <TableContainer sx={{border: `1px solid ${colors.cardBorder}`, borderRadius: "4px"}}>
                            <Table size="small" aria-label={t("DETAIL_SUBSCRIPTIONS")}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t("TENANTS_COLUMN_PLAN")}</TableCell>
                                        <TableCell>{t("DETAIL_SUB_STATUS")}</TableCell>
                                        <TableCell>{t("DETAIL_SUB_PAID_THROUGH")}</TableCell>
                                        <TableCell>{t("DETAIL_SUB_ACCESS_UNTIL")}</TableCell>
                                        <TableCell>{t("DETAIL_SUB_CANCELS_ON")}</TableCell>
                                        <TableCell align="right">{t("DETAIL_SUB_MAX_EMPLOYEES")}</TableCell>
                                        <TableCell align="right">{t("DETAIL_SUB_AMOUNT_PAID")}</TableCell>
                                        <TableCell sx={{width: 40}} />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {subscriptions.map((subscription) => {
                                        const lifecycle = getSubscriptionLifecycleSummary(subscription);
                                        const gate = deleteGate(subscription, subscriptions, now);
                                        return (
                                                <TableRow
                                                        key={subscription.id}
                                                        hover
                                                        selected={selectedId === subscription.id}
                                                        tabIndex={0}
                                                        sx={{cursor: "pointer"}}
                                                        onClick={() => setEditor({mode: "edit", subscription})}
                                                        onKeyDown={(event) => {
                                                            if (event.key === "Enter") setEditor({mode: "edit", subscription});
                                                        }}
                                                >
                                                    <TableCell sx={{fontWeight: 500, color: colors.ink}}>{planName(subscription.planId)}</TableCell>
                                                    <TableCell>
                                                        <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                                                            {lifecycle.status && (
                                                                    <StatusChip tone={STATUS_TONES[lifecycle.status]}>{t(subscriptionStatusLabelKey(lifecycle.status))}</StatusChip>
                                                            )}
                                                            {lifecycle.paymentActionRequired && (
                                                                    <StatusChip tone="error">{t(subscriptionStatusLabelKey("paymentActionRequired"))}</StatusChip>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>{formatDate(lifecycle.paidThrough)}</TableCell>
                                                    <TableCell>{lifecycle.accessThrough ? formatDate(lifecycle.accessThrough) : ""}</TableCell>
                                                    <TableCell sx={{color: colors.expiring}}>{lifecycle.cancelsOn ? formatDate(lifecycle.cancelsOn) : ""}</TableCell>
                                                    <TableCell align="right">{subscription.maxEmployees}</TableCell>
                                                    <TableCell align="right">{formatEuroCents(subscription.amountPaidInCents)}</TableCell>
                                                    <TableCell sx={{py: 0}}>
                                                        <Tooltip title={gate.allowed ? t("DETAIL_DELETE_SUBSCRIPTION") : t(gate.reasonKey)}>
                                                            <span>
                                                                <IconButton
                                                                        size="small"
                                                                        color="error"
                                                                        disabled={!gate.allowed}
                                                                        aria-label={t("DETAIL_DELETE_SUBSCRIPTION")}
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                            void handleDelete(subscription);
                                                                        }}
                                                                >
                                                                    <DeleteOutlineIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                )}

                {editor && (
                        <SubscriptionEditor
                                key={editor.mode === "edit" ? editor.subscription.id : "create"}
                                tenantId={tenantId}
                                plans={plans}
                                subscription={editor.mode === "edit" ? editor.subscription : null}
                                planName={planName}
                                onCancel={() => setEditor(null)}
                                onSaved={() => {
                                    setEditor(null);
                                    onChanged();
                                }}
                        />
                )}
            </SectionCard>
    );
}

function SubscriptionEditor({
                                tenantId,
                                plans,
                                subscription,
                                planName,
                                onCancel,
                                onSaved,
                            }: {
    tenantId: string;
    plans: Plan[];
    subscription: Subscription | null;
    planName: (planId: string) => string;
    onCancel: () => void;
    onSaved: () => void;
}) {
    const {t} = useTranslation();
    const isEdit = subscription !== null;
    const [planId, setPlanId] = useState(subscription?.planId ?? "");
    const [maxEmployees, setMaxEmployees] = useState(subscription?.maxEmployees ?? 1);
    const [startDate, setStartDate] = useState(subscription?.startDate ? subscription.startDate.slice(0, 10) : new Date().toISOString().slice(0, 10));
    const [isFreePlan, setIsFreePlan] = useState(!subscription?.amountDueInCents);
    const [amountDue, setAmountDue] = useState(subscription ? subscription.amountDueInCents / 100 : 0);
    const [amountPaid, setAmountPaid] = useState(subscription ? subscription.amountPaidInCents / 100 : 0);
    const [activeUntil, setActiveUntil] = useState(subscription?.activeUntil?.slice(0, 10) ?? "");
    const [paidUntil, setPaidUntil] = useState(subscription?.paidUntil?.slice(0, 10) ?? "");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pendingUpgrade, setPendingUpgrade] = useState<{ preview: SubscriptionUpgradePreview, outdated: boolean } | null>(null);

    function subscriptionRequest(): AdminSubscriptionSaveRequest {
        return {
            planId,
            startDate: new Date(startDate).toISOString(),
            paidUntil,
            activeUntil,
            maxEmployees,
            amountDueInCents: isFreePlan ? 0 : Math.round(amountDue * 100),
            amountPaidInCents: isFreePlan ? 0 : Math.round(amountPaid * 100),
        };
    }

    // Shows staff what saving changes for the tenant; true when they still have to confirm it
    async function needsConfirmation(outdated: boolean): Promise<boolean> {
        const preview = await previewSubscriptionUpgrade(tenantId, {subscriptionId: subscription?.id, subscription: subscriptionRequest()});
        if (!hasPreviewChanges(preview)) return false;
        setPendingUpgrade({preview, outdated});
        return true;
    }

    // after a save was refused as outdated: shows the changes to confirm now, or says the subscription changed
    async function confirmAgain() {
        try {
            if (await needsConfirmation(true)) return;
        } catch {
            // the message below tells staff what to do either way
        }
        setPendingUpgrade(null);
        setError(t("SUBSCRIPTION_CHANGED_MEANWHILE"));
    }

    async function save(body: AdminSubscriptionSaveRequest) {
        setError(null);
        setSubmitting(true);
        try {
            if (subscription) await updateSubscription(tenantId, subscription.id, body);
            else await createSubscription(tenantId, body);
            setPendingUpgrade(null);
            onSaved();
        } catch (e) {
            const outdated = e instanceof ApiError && (e.details as { error?: string } | undefined)?.error === "UpgradePreviewOutdated";
            if (outdated) {
                await confirmAgain();
            } else {
                setPendingUpgrade(null);
                setError(e instanceof Error ? e.message : t("DETAIL_SAVE_FAILED"));
            }
        } finally {
            setSubmitting(false);
        }
    }

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);
        setSubmitting(true);
        try {
            if (await needsConfirmation(false)) return;
        } catch (e) {
            setError(e instanceof Error ? e.message : t("DETAIL_SAVE_FAILED"));
            return;
        } finally {
            setSubmitting(false);
        }
        await save(subscriptionRequest());
    };

    const confirmUpgrade = (skipUpgradeDefaults: boolean) => {
        if (!pendingUpgrade) return;
        void save({...subscriptionRequest(), upgradeConfirmation: {fingerprint: pendingUpgrade.preview.fingerprint, skipUpgradeDefaults}});
    };

    return (
            <Box
                    component="form"
                    onSubmit={handleSubmit}
                    aria-label={isEdit ? t("DETAIL_EDIT_SUBSCRIPTION") : t("DETAIL_ADD_SUBSCRIPTION")}
                    sx={{mt: 2, p: 2, border: `1px solid ${colors.cardBorder}`, borderRadius: "4px", backgroundColor: colors.tableHeaderAlt}}
            >
                <Box sx={{display: "flex", alignItems: "baseline", gap: 1, mb: 1.5}}>
                    <Typography variant="h6" component="h3">
                        {isEdit ? t("DETAIL_EDIT_SUBSCRIPTION") : t("DETAIL_ADD_SUBSCRIPTION")}
                    </Typography>
                    {subscription && (
                            <Typography variant="body2" sx={{color: colors.muted}}>
                                {t("DETAIL_SUBSCRIPTION_FROM", {plan: planName(subscription.planId), date: formatDate(subscription.startDate)})}
                            </Typography>
                    )}
                </Box>
                {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
                {pendingUpgrade && (
                        <UpgradePreviewDialog
                                key={pendingUpgrade.preview.fingerprint}
                                preview={pendingUpgrade.preview}
                                outdated={pendingUpgrade.outdated}
                                saving={submitting}
                                onConfirm={confirmUpgrade}
                                onCancel={() => setPendingUpgrade(null)}
                        />
                )}
                <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "repeat(2, 1fr)", lg: "2fr 1fr 1fr 1fr 1fr"}, gap: 1.5}}>
                    <FormControl size="small" required fullWidth>
                        <InputLabel id="sub-plan-label">{t("TENANTS_COLUMN_PLAN")}</InputLabel>
                        <Select labelId="sub-plan-label" value={planId} label={t("TENANTS_COLUMN_PLAN")} onChange={(e) => setPlanId(e.target.value)}>
                            <MenuItem value="">{t("DETAIL_SELECT_PLAN")}</MenuItem>
                            {plans.map((plan) => (
                                    <MenuItem key={plan.id} value={plan.id}>{plan.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                            label={t("DETAIL_MAX_EMPLOYEES")}
                            type="number"
                            size="small"
                            slotProps={{htmlInput: {min: 1}}}
                            value={maxEmployees}
                            onChange={(e) => setMaxEmployees(Number(e.target.value))}
                    />
                    <TextField label={t("DETAIL_START_DATE")} type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    <TextField label={t("DETAIL_PAID_UNTIL")} type="date" required size="small" value={paidUntil} onChange={(e) => setPaidUntil(e.target.value)} />
                    <TextField label={t("DETAIL_ACTIVE_UNTIL")} type="date" required size="small" value={activeUntil} onChange={(e) => setActiveUntil(e.target.value)} />
                </Box>
                <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1.5, mt: 1.5}}>
                    <FormControlLabel
                            control={<Switch checked={isFreePlan} onChange={(e) => setIsFreePlan(e.target.checked)} />}
                            label={t("DETAIL_FREE_PLAN")}
                            sx={{mr: 2, mb: 0.5, ml: 0}}
                    />
                    {!isFreePlan && (
                            <>
                                <TextField
                                        label={t("DETAIL_AMOUNT_DUE")}
                                        type="number"
                                        size="small"
                                        slotProps={{htmlInput: {min: 0, step: 0.01}}}
                                        value={amountDue}
                                        onChange={(e) => setAmountDue(Number(e.target.value))}
                                        sx={{width: 150}}
                                />
                                <TextField
                                        label={t("DETAIL_AMOUNT_PAID")}
                                        type="number"
                                        size="small"
                                        slotProps={{htmlInput: {min: 0, step: 0.01}}}
                                        value={amountPaid}
                                        onChange={(e) => setAmountPaid(Number(e.target.value))}
                                        sx={{width: 150}}
                                />
                            </>
                    )}
                    <Box sx={{flex: 1}} />
                    <Button variant="outlined" onClick={onCancel}>{t("CANCEL")}</Button>
                    <Button type="submit" variant="contained" disabled={submitting || !planId}>
                        {submitting ? t("DETAIL_SAVING") : t("DETAIL_SAVE_SUBSCRIPTION")}
                    </Button>
                </Box>
            </Box>
    );
}
